import { Text } from "components/text";
import {
  DropzoneOptions,
  FileRejection,
  useDropzone,
  DropEvent,
} from "react-dropzone";
import { useTranslation } from "react-i18next";
import { Icon, IconProps } from "components/icon/Icon";
import LoadingSpinner from "components/loading/LoadingSpinner";
import { useCallback } from "react";

interface FileUploadDropZoneProps extends DropzoneOptions {
  maxSizeMB?: number;
  icon?: IconProps["name"];
  isLoading?: boolean;
  error?: string;
}

export const FileUploadDropZone = ({
  maxSizeMB,
  icon = "upload",
  isLoading = false,
  disabled,
  error: errorProp,
  ...rest
}: FileUploadDropZoneProps) => {
  const { t } = useTranslation(["components"]);

  const getErrorMessage = useCallback(
    // biome-ignore lint/suspicious/noShadow: the outer fileRejections from useDropzone is declared after this callback
    (fileRejections: readonly FileRejection[]): string => {
      if (!fileRejections.length) return "";

      const errorCode = fileRejections[0].errors[0].code;
      switch (errorCode) {
        case "file-invalid-type":
          return t("components:fileUpload.dropZone.errors.invalidType");
        case "file-too-large":
          return t("components:fileUpload.dropZone.errors.tooLarge", {
            maxSizeMB,
          });
        case "too-many-files":
          return t("components:fileUpload.dropZone.errors.tooManyFiles");
        default:
          return t("components:fileUpload.dropZone.errors.unknown");
      }
    },
    [t, maxSizeMB],
  );

  const { onDrop: onDropProp, ...restOptions } = rest;

  const dropzoneOptions: DropzoneOptions = {
    disabled: isLoading || disabled,
    maxSize: maxSizeMB ? maxSizeMB * 1024 * 1024 : undefined,
    ...restOptions,
  };

  const handleDrop = useCallback(
    (acceptedFiles: File[], rejections: FileRejection[], event: DropEvent) => {
      onDropProp?.(acceptedFiles, rejections, event);
    },
    [onDropProp],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({ ...dropzoneOptions, onDrop: handleDrop });

  const error =
    fileRejections.length > 0
      ? getErrorMessage(fileRejections)
      : errorProp || null;

  const getDisplayMessage = useCallback((): string => {
    if (isLoading) {
      return t("components:fileUpload.dropZone.loading");
    }
    if (isDragActive) {
      return t("components:fileUpload.dropZone.dropFiles");
    }
    return t("components:fileUpload.dropZone.message");
  }, [isLoading, isDragActive, t]);

  // Determine border color based on state
  let borderColor = "border-secondary-200";
  if (error) {
    borderColor = "border-system-error";
  } else if (isLoading) {
    borderColor = "border-secondary-300";
  }

  // Determine icon color based on state
  let iconColor = "text-primary-600";
  if (isDragActive) {
    iconColor = "text-primary-800";
  } else if (error) {
    iconColor = "text-system-error";
  }

  return (
    <div
      {...getRootProps()}
      className={`items-center flex p-12 bg-secondary-25 border border-dashed ${borderColor} rounded-xl flex-col relative ${isLoading || disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      data-testid="drop-zone"
    >
      <input
        {...getInputProps()}
        data-testid="file-upload"
        disabled={isLoading}
      />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Icon name={icon} iconClassName={iconColor} size="large" />
      )}
      <div className="text-center mt-2">
        <p>{getDisplayMessage()}</p>
        {maxSizeMB && !error && (
          <Text variant="subtle" className="mt-1 mb-4">
            {t(`components:fileUpload.dropZone.maxSize`, {
              maxSizeMB,
            })}
          </Text>
        )}
        {error && (
          <Text variant="error" className="mt-1 mb-4">
            {error}
          </Text>
        )}
      </div>
    </div>
  );
};
